import { useCallback, useEffect, useMemo, useState } from 'react'

import { api } from '../api'
import Why from '../components/Why'
import { t } from '../i18n'
import { confirmAction, haptic, notify } from '../telegram'
import type { Game, Match, MatchPlayer, MatchPreview, PlayerMe, SetScore } from '../types'

interface Props {
  game: Game
  me: PlayerMe
}

function fmtDelta(d: number): string {
  const sign = d > 0 ? '+' : d < 0 ? '−' : '±'
  return `${sign}${Math.abs(d).toFixed(2)}`
}

function deltaClass(d: number): string {
  return d > 0 ? 'badge' : d < 0 ? 'badge warn' : 'badge neutral'
}

/** «6:4»; супертай-брейк — «[10:8]», без очков — «[ТБ]» (победитель выделен жирным в составе) */
function fmtSet(s: SetScore): string {
  if (s.tiebreak && Math.max(s.a, s.b) <= 1) return `[${t().tbShort}]`
  return s.tiebreak ? `[${s.a}:${s.b}]` : `${s.a}:${s.b}`
}

// --- Карточка внесённого матча ---

function TeamLine({ players, bold, meId }: { players: MatchPlayer[]; bold: boolean; meId: number }) {
  return (
    <div className="row between" style={{ padding: '4px 0' }}>
      <span style={{ fontWeight: bold ? 700 : 400 }}>
        {players.map((p) => (p.id === meId ? `${p.first_name} (${t().youLabel})` : p.first_name)).join(' + ')}
      </span>
      <span className="row" style={{ gap: 4 }}>
        {players.map((p) =>
          p.delta == null ? null : (
            <span key={p.id} className={deltaClass(p.delta)} title={p.first_name}>
              {p.projected ? '≈' : ''}
              {fmtDelta(p.delta)}
            </span>
          ),
        )}
      </span>
    </div>
  )
}

export function MatchCard({ match, meId, onChange }: { match: Match; meId: number; onChange: () => void }) {
  const [busy, setBusy] = useState(false)

  async function act(fn: () => Promise<unknown>) {
    setBusy(true)
    haptic('medium')
    try {
      await fn()
      onChange()
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setBusy(false)
    }
  }

  const hoursLeft = match.auto_confirm_at
    ? Math.max(1, Math.ceil((new Date(match.auto_confirm_at).getTime() - Date.now()) / 3.6e6))
    : 0

  const disputeHours = match.dispute_until
    ? Math.max(0, Math.ceil((new Date(match.dispute_until).getTime() - Date.now()) / 3.6e6))
    : 0

  // Объяснение показываем зрителю про него самого; постороннему — про победителей
  const all = [...match.team_a, ...match.team_b]
  const mine = all.find((p) => p.id === meId)
  const focus = mine ?? (match.winner === 'b' ? match.team_b[0] : match.team_a[0])
  const projected = all.some((p) => p.projected)

  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid var(--line)' }}>
      <div className="row between" style={{ marginBottom: 4 }}>
        <b style={{ fontSize: 17, letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
          {match.sets.map(fmtSet).join('  ')}
        </b>
        {match.status === 'pending' && <span className="badge neutral">{t().pendingScore(hoursLeft)}</span>}
        {match.status === 'confirmed' && <span className="badge">{t().confirmedScore}</span>}
        {match.status === 'disputed' && <span className="badge warn">{t().disputedScore}</span>}
      </div>

      <TeamLine players={match.team_a} bold={match.winner === 'a'} meId={meId} />
      <TeamLine players={match.team_b} bold={match.winner === 'b'} meId={meId} />

      {match.friendly && <div className="muted small">{t().friendlyNote}</div>}
      {focus?.why && match.status !== 'disputed' && (
        <>
          {projected && <div className="muted small">≈ {t().projected}</div>}
          <Why why={focus.why} mine={Boolean(mine)} />
        </>
      )}

      {(match.can_confirm || match.can_withdraw || match.can_dispute) && (
        <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
          {match.can_confirm && (
            <button disabled={busy} onClick={() => act(() => api.confirmMatch(match.id))}>
              {t().confirmScore}
            </button>
          )}
          {match.can_dispute && (
            <button
              className="ghost"
              disabled={busy}
              onClick={async () => {
                const text = match.status === 'confirmed' ? t().disputeConfirmed : t().disputeConfirm
                if (await confirmAction(text)) await act(() => api.disputeMatch(match.id))
              }}
            >
              {t().disputeScore}
            </button>
          )}
          {match.can_withdraw && (
            <button className="ghost" disabled={busy} onClick={() => act(() => api.withdrawMatch(match.id))}>
              {t().withdrawScore}
            </button>
          )}
        </div>
      )}
      {match.status === 'confirmed' && match.can_dispute && disputeHours > 0 && (
        <div className="muted small" style={{ marginTop: 4 }}>
          {t().disputeUntil(disputeHours)}
        </div>
      )}
    </div>
  )
}

// --- Быстрый ввод счёта ---

type Pick = { a: number; b: number } | null

// Все корректные счета сета в паделе. Счёт «на время» (5:3, 4:4) —
// через «Другой счёт».
const WINS: [number, number][] = [[6, 0], [6, 1], [6, 2], [6, 3], [6, 4], [7, 5], [7, 6]]
const LOSSES: [number, number][] = WINS.map(([a, b]) => [b, a])

function SetPicker({ label, value, onPick }: { label: string; value: Pick; onPick: (v: Pick) => void }) {
  const same = (a: number, b: number) => value?.a === a && value?.b === b
  const row = (title: string, list: [number, number][]) => (
    <div className="score-row">
      <span className="muted small">{title}</span>
      <div className="score-grid">
        {list.map(([a, b]) => (
          <button
            key={`${a}:${b}`}
            className="chip score"
            aria-pressed={same(a, b)}
            onClick={() => {
              haptic()
              onPick(same(a, b) ? null : { a, b })
            }}
          >
            {a}:{b}
          </button>
        ))}
      </div>
    </div>
  )
  return (
    <div className="set-block">
      <div className="row between">
        <b>{label}</b>
        <span className="muted">{value ? `${value.a}:${value.b}` : t().pickSet}</span>
      </div>
      {row(t().wonSet, WINS)}
      {row(t().lostSet, LOSSES)}
    </div>
  )
}

const EMPTY_ROW = { a: '', b: '', tiebreak: false }

function ScoreForm({ game, me, onDone }: { game: Game; me: PlayerMe; onDone: () => void }) {
  const roster = game.slots.filter((s) => s.status === 'joined').map((s) => s.player)
  const others = roster.filter((p) => p.id !== me.id)

  // Если до матча выбрали места на корте — партнёр уже известен
  const [partner, setPartner] = useState<number | null>(() => {
    const slots = game.slots.filter((s) => s.status === 'joined')
    const mySpot = slots.find((s) => s.player.id === me.id)?.court_spot
    if (!mySpot) return null
    const mate = mySpot <= 2 ? (mySpot === 1 ? 2 : 1) : mySpot === 3 ? 4 : 3
    return slots.find((s) => s.court_spot === mate)?.player.id ?? null
  })
  const [picked, setPicked] = useState<number[]>([])
  const [manual, setManual] = useState(false)
  // Быстрый режим
  const [s1, setS1] = useState<Pick>(null)
  const [s2, setS2] = useState<Pick>(null)
  const [s3, setS3] = useState<Pick>(null)
  const [thirdMode, setThirdMode] = useState<'tb' | 'full'>('tb')
  const [tbWinner, setTbWinner] = useState<'a' | 'b' | null>(null)
  const [tbPts, setTbPts] = useState({ a: '', b: '' })
  // Ручной режим — для игры на время и нестандартных счетов
  const [rows, setRows] = useState([{ ...EMPTY_ROW }, { ...EMPTY_ROW }, { ...EMPTY_ROW, tiebreak: true }])

  const [preview, setPreview] = useState<MatchPreview | null>(null)
  const [saving, setSaving] = useState(false)

  // Ровно четверо в составе — соперники определяются сами
  const candidates = others.filter((p) => p.id !== partner)
  const opponents = useMemo(
    () => (candidates.length === 2 ? candidates.map((p) => p.id) : picked),
    [candidates, picked],
  )

  const splitAfterTwo = Boolean(s1 && s2 && s1.a > s1.b !== s2.a > s2.b)

  const sets: SetScore[] | null = useMemo(() => {
    if (manual) {
      const list = rows
        .filter((r) => r.a !== '' && r.b !== '')
        .map((r) => ({ a: Number(r.a), b: Number(r.b), tiebreak: r.tiebreak }))
      return list.length ? list : null
    }
    if (!s1 || !s2) return null
    const base: SetScore[] = [
      { ...s1, tiebreak: false },
      { ...s2, tiebreak: false },
    ]
    if (!splitAfterTwo) return base
    if (thirdMode === 'full') return s3 ? [...base, { ...s3, tiebreak: false }] : null
    if (!tbWinner) return null
    const pa = Number(tbPts.a)
    const pb = Number(tbPts.b)
    const pointsOk =
      tbPts.a !== '' && tbPts.b !== '' && (tbWinner === 'a' ? pa > pb : pb > pa)
    // Без очков тай-брейк = один гейм победителю: на рейтинг влияет только
    // сам факт победы в нём, точные очки не нужны
    const tb = pointsOk ? { a: pa, b: pb } : tbWinner === 'a' ? { a: 1, b: 0 } : { a: 0, b: 1 }
    return [...base, { ...tb, tiebreak: true }]
  }, [manual, rows, s1, s2, s3, splitAfterTwo, thirdMode, tbWinner, tbPts])

  const ready = Boolean(partner && opponents.length === 2 && sets)

  // Превью изменений уровня — как только известны пары и счёт.
  // Зависимость — строковый ключ: массивы пересоздаются на каждом рендере,
  // и эффект по ним уходил бы в бесконечный цикл запросов.
  const previewKey = ready && partner && sets ? JSON.stringify([partner, opponents, sets]) : ''
  useEffect(() => {
    if (!previewKey) {
      setPreview(null)
      return
    }
    const [p, opp, sc] = JSON.parse(previewKey) as [number, number[], SetScore[]]
    let alive = true
    const timer = window.setTimeout(() => {
      api
        .previewMatch(game.id, { team_a: [me.id, p], team_b: opp, sets: sc })
        .then((res) => alive && setPreview(res))
        .catch(() => alive && setPreview(null))
    }, 250)
    return () => {
      alive = false
      window.clearTimeout(timer)
    }
  }, [previewKey, game.id, me.id])

  function toggleOpponent(id: number) {
    setPicked((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length < 2 ? [...cur, id] : cur))
  }

  function setCell(i: number, key: 'a' | 'b', value: string) {
    setRows((cur) => cur.map((r, idx) => (idx === i ? { ...r, [key]: value.replace(/\D/g, '').slice(0, 2) } : r)))
  }

  async function submit() {
    if (!partner) return notify(t().partner)
    if (opponents.length !== 2) return notify(t().opponents)
    if (!sets) return notify(t().pickSet)

    setSaving(true)
    haptic('medium')
    try {
      await api.submitMatch(game.id, { team_a: [me.id, partner], team_b: opponents, sets })
      onDone()
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setSaving(false)
    }
  }

  const cell = { width: 56, textAlign: 'center' as const, padding: '10px 4px', fontSize: 17, fontWeight: 700 }
  const myWhy = preview?.team_a.find((p) => p.id === me.id)?.why
  const resultLabel = preview
    ? preview.winner === 'a'
      ? t().win
      : preview.winner === 'b'
        ? t().loss
        : t().draw
    : ''

  return (
    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
      <label>{t().partner}</label>
      <div className="chips">
        {others.map((p) => (
          <button
            key={p.id}
            className="chip"
            aria-pressed={partner === p.id}
            onClick={() => {
              setPartner(p.id)
              setPicked([])
            }}
          >
            {p.first_name}
          </button>
        ))}
      </div>

      {partner && (
        <>
          <label>{t().opponents}</label>
          <div className="chips">
            {candidates.map((p) => (
              <button
                key={p.id}
                className="chip"
                aria-pressed={opponents.includes(p.id)}
                disabled={candidates.length === 2}
                onClick={() => toggleOpponent(p.id)}
              >
                {p.first_name}
              </button>
            ))}
          </div>
        </>
      )}

      <div className="row between" style={{ marginTop: 16 }}>
        <span className="title">{t().scoreLabel}</span>
        <div className="chips">
          <button className="chip" aria-pressed={!manual} onClick={() => setManual(false)}>
            {t().quickScore}
          </button>
          <button className="chip" aria-pressed={manual} onClick={() => setManual(true)}>
            {t().otherScore}
          </button>
        </div>
      </div>

      {!manual && (
        <>
          <SetPicker label={t().setN(1)} value={s1} onPick={setS1} />
          <SetPicker label={t().setN(2)} value={s2} onPick={setS2} />

          {splitAfterTwo && (
            <div className="set-block">
              <div className="row between">
                <b>{t().thirdSet}</b>
                <div className="chips">
                  <button className="chip" aria-pressed={thirdMode === 'tb'} onClick={() => setThirdMode('tb')}>
                    {t().superTiebreak}
                  </button>
                  <button className="chip" aria-pressed={thirdMode === 'full'} onClick={() => setThirdMode('full')}>
                    {t().fullSet}
                  </button>
                </div>
              </div>

              {thirdMode === 'full' ? (
                <SetPicker label={t().setN(3)} value={s3} onPick={setS3} />
              ) : (
                <>
                  <div className="muted small" style={{ margin: '10px 0 6px' }}>
                    {t().tbWinner}
                  </div>
                  <div className="chips">
                    <button className="chip" aria-pressed={tbWinner === 'a'} onClick={() => setTbWinner('a')}>
                      {t().us}
                    </button>
                    <button className="chip" aria-pressed={tbWinner === 'b'} onClick={() => setTbWinner('b')}>
                      {t().them}
                    </button>
                  </div>
                  <div className="muted small" style={{ margin: '10px 0 6px' }}>
                    {t().tbPoints}
                  </div>
                  <div className="row" style={{ gap: 8 }}>
                    <input
                      inputMode="numeric"
                      aria-label={`${t().superTiebreak} ${t().us}`}
                      placeholder={t().us}
                      value={tbPts.a}
                      onChange={(e) => setTbPts((p) => ({ ...p, a: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                      style={cell}
                    />
                    <input
                      inputMode="numeric"
                      aria-label={`${t().superTiebreak} ${t().them}`}
                      placeholder={t().them}
                      value={tbPts.b}
                      onChange={(e) => setTbPts((p) => ({ ...p, b: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                      style={cell}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {manual && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'center', marginTop: 14 }}>
          <span />
          <span className="muted" style={{ width: 56, textAlign: 'center' }}>{t().us}</span>
          <span className="muted" style={{ width: 56, textAlign: 'center' }}>{t().them}</span>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'contents' }}>
              <span className="muted">
                {r.tiebreak ? t().superTiebreak : t().setN(i + 1)}
                {i === 2 && (
                  <input
                    type="checkbox"
                    aria-label={t().superTiebreak}
                    checked={r.tiebreak}
                    onChange={(e) =>
                      setRows((cur) => cur.map((x, idx) => (idx === 2 ? { ...x, tiebreak: e.target.checked } : x)))
                    }
                    style={{ width: 18, marginLeft: 8, verticalAlign: 'middle' }}
                  />
                )}
              </span>
              <input inputMode="numeric" aria-label={`${t().setN(i + 1)} ${t().us}`} value={r.a} onChange={(e) => setCell(i, 'a', e.target.value)} style={cell} />
              <input inputMode="numeric" aria-label={`${t().setN(i + 1)} ${t().them}`} value={r.b} onChange={(e) => setCell(i, 'b', e.target.value)} style={cell} />
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="preview">
          <b>{t().summary(resultLabel, preview.sets_a, preview.sets_b, preview.games_a, preview.games_b)}</b>
          <div className="muted small" style={{ margin: '8px 0 4px' }}>
            {preview.friendly ? t().friendlyNote : t().previewTitle}
          </div>
          {!preview.friendly && [...preview.team_a, ...preview.team_b].map((p) => (
            <div key={p.id} className="row between" style={{ padding: '3px 0' }}>
              <span>
                {p.first_name}
                {p.id === me.id ? ` (${t().youLabel})` : ''}
              </span>
              <span className="muted">
                {p.level_before?.toFixed(2)} → <b>{p.level_after?.toFixed(2)}</b>{' '}
                {p.delta != null && <span className={deltaClass(p.delta)}>{fmtDelta(p.delta)}</span>}
              </span>
            </div>
          ))}
          {myWhy && <Why why={myWhy} />}
        </div>
      )}

      <button style={{ width: '100%', marginTop: 14 }} disabled={saving || !ready} onClick={submit}>
        {saving ? t().loading : t().submitScore}
      </button>
    </div>
  )
}

/** Блок «Результат» на экране игры. Показывается после начала игры. */
export default function MatchResults({ game, me }: Props) {
  const [matches, setMatches] = useState<Match[] | null>(null)
  const [formOpen, setFormOpen] = useState(false)

  const started = new Date(game.starts_at).getTime() <= Date.now()
  const participant = game.my_status === 'joined'
  const enoughPlayers = game.joined_count >= 4

  const load = useCallback(async () => {
    setMatches(await api.matches(game.id))
  }, [game.id])

  useEffect(() => {
    if (started) void load()
  }, [started, load])

  if (!started || game.status === 'cancelled') return null

  return (
    <div className="card">
      <div className="row between">
        <div className="title">{t().result}</div>
        {participant && enoughPlayers && !formOpen && (
          <button className="ghost" onClick={() => setFormOpen(true)}>
            + {t().enterScore}
          </button>
        )}
      </div>
      <div className="muted" style={{ marginTop: 4 }}>
        {t().scoreHint}
        {!game.rated && ` ${t().friendlyNote}`}
      </div>

      {formOpen && (
        <ScoreForm
          game={game}
          me={me}
          onDone={() => {
            setFormOpen(false)
            void load()
          }}
        />
      )}

      {matches?.map((m) => <MatchCard key={m.id} match={m} meId={me.id} onChange={load} />)}
    </div>
  )
}
